import { Form, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Button } from "@/components/ui/button"
import { DialogFooter } from "@/components/ui/dialog"
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { formSchema } from "./send-amount.schema"
import AmountField from "@/pay/amount.field"
import BalancesSelect from "@/pay/with-balance/balances.select"
import { Network } from "@/config/tokens"
import { Balance, useBalances } from "@/api/balances"
import Loader from "@/components/layout/loader"

interface SendAmountFormProps {
  onSubmit: (data: z.infer<typeof formSchema>) => void;
  onBack?: () => void;
  networks: Network[];
  amount?: string;
}

export default function SendAmountForm({
  onSubmit,
  networks,
  onBack,
  amount = "",
}: SendAmountFormProps) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      amount,
    },
  })
  const { balances, isLoading } = useBalances()
  const token = form.watch("token")
  const network = form.watch("network")

  if (isLoading) return <Loader message="Loading..." />
  const balance = (balances || []).find(
    (balance: Balance) =>
      balance.token === token && balance.network === network,
  )
  const disableSend =
    !form.watch("amount") ||
    !form.watch("network") ||
    !balance ||
    balance.available < Number(form.watch("amount"))

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4 pt-4">
          <AmountField
            token={form.watch("token")}
            presetAmounts={[10, 50, 100]}
          />
          <FormItem>
            <FormLabel required>Network</FormLabel>
            <BalancesSelect
              selected={{
                network: form.watch("network"),
                token: form.watch("token"),
              }}
              onSelect={(network, token) => {
                form.setValue("network", network)
                form.setValue("token", token)
              }}
              amount={Number(form.watch("amount"))}
              networks={networks}
            />
            <FormMessage />
          </FormItem>
        </div>
        <DialogFooter className="mt-0">
          <Button variant="outline" onClick={onBack} disabled={!onBack}>
            Back
          </Button>
          <Button
            type="submit"
            disabled={disableSend}
            data-testid="send-preview-button"
          >
            Preview
          </Button>
        </DialogFooter>
      </form>
    </Form>
  )
}
